import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StrategicOption } from "./StrategicOptionCard";
import { cn } from "@/lib/utils";
import { BarChart3, Info } from "lucide-react";
import { calculateAlignmentScore, calculateEffectiveExposure } from "@/lib/utils/alignmentScore";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface OptionCompareMatrixProps {
  options: StrategicOption[];
  sessionMeta?: Record<string, any>;
}

const formatEur = (min?: number, max?: number) => {
  if (min == null && max == null) return "—";
  const fmt = (v: number) => {
    if (v >= 1000) return `${Math.round(v / 1000)}k`;
    return v.toLocaleString("de-DE");
  };
  if (min != null && max != null) return `${fmt(min)}–${fmt(max)}`;
  if (min != null) return `ab ${fmt(min)}`;
  return `bis ${fmt(max!)}`;
};

const reversibilityColor = (level?: string) => {
  if (level === "hoch") return "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10";
  if (level === "mittel") return "text-amber-600 dark:text-amber-400 bg-amber-500/10";
  if (level === "gering") return "text-destructive bg-destructive/10";
  return "text-muted-foreground bg-muted/50";
};

const reversibilityLabel = (level?: string) => {
  if (level === "hoch") return "Hoch";
  if (level === "mittel") return "Mittel";
  if (level === "gering") return "Gering";
  return "—";
};

const inactionLabel = (effect?: string) => {
  const map: Record<string, { label: string; color: string }> = {
    eliminates: { label: "Eliminiert", color: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10" },
    reduces: { label: "Reduziert", color: "text-amber-600 dark:text-amber-400 bg-amber-500/10" },
    shifts: { label: "Verschiebt", color: "text-amber-600 dark:text-amber-400 bg-amber-500/10" },
    ignores: { label: "Ignoriert", color: "text-destructive bg-destructive/10" },
  };
  return map[effect || ""] || { label: "—", color: "text-muted-foreground bg-muted/50" };
};

const capacityColor = (pct?: number) => {
  if (pct == null) return "text-muted-foreground";
  if (pct <= 20) return "text-emerald-600 dark:text-emerald-400";
  if (pct <= 40) return "text-amber-600 dark:text-amber-400";
  return "text-destructive";
};

const totalCapacityColor = (pct?: number | null) => {
  if (pct == null) return "text-muted-foreground";
  if (pct <= 60) return "text-emerald-600 dark:text-emerald-400";
  if (pct <= 80) return "text-amber-600 dark:text-amber-400";
  return "text-destructive";
};

export const OptionCompareMatrix = ({ options, sessionMeta }: OptionCompareMatrixProps) => {
  if (options.length < 2) return null;

  const hasEconomicData = options.some(o => 
    o.estimated_cost_min != null || o.capacity_requirement_percent != null || o.reversibility_level
  );
  if (!hasEconomicData) return null;

  const labels = ["Option A", "Option B", "Option C"];
  const phase1_2 = sessionMeta?.phase1_2_conflict || {};
  const phase1_3 = sessionMeta?.phase1_3_irreversibility || {};
  const validationDeadlineDays = phase1_3?.validation_deadline_days;
  const baselineCapacity = phase1_2?.capacity_percent;
  const dailyExposure = phase1_3?.daily_exposure 
    || (phase1_3?.exposure_until_validation && validationDeadlineDays 
        ? Math.round(phase1_3.exposure_until_validation / validationDeadlineDays) 
        : undefined);
  const commitMode = sessionMeta?.phase2_commit_mode === true;

  // System observations (neutral, structural only) — now using numeric days
  const observations: string[] = [];

  if (validationDeadlineDays) {
    options.forEach((o, i) => {
      // Prefer numeric field, fall back to regex
      const signalDays = o.time_to_first_signal_days;
      if (signalDays != null && signalDays > validationDeadlineDays) {
        observations.push(`${labels[i]} liefert kein Marktsignal vor der Validierungsdeadline (${validationDeadlineDays} Tage).`);
      }
    });
  }

  if (options.every(o => o.reversibility_level === "gering")) {
    observations.push("Keine der Optionen bietet einen einfachen Rückweg.");
  }

  options.forEach((o, i) => {
    if (o.inaction_cost_effect === "eliminates" && o.reversibility_level === "hoch") {
      observations.push(`${labels[i]} adressiert die Inaction-Kosten bei geringstem Lock-In-Risiko.`);
    }
  });

  // Completeness observation
  const hasStrategyShift = options.some(o => o.is_strategy_shift);
  const hasConstraintBreak = options.some(o => o.is_constraint_break);
  if (hasStrategyShift) {
    observations.push("Mindestens eine Option enthält einen Strategiewechsel gegenüber dem Phase-1-Commitment.");
  }

  // Calculate derived metrics per option
  const derivedMetrics = options.map(o => {
    const totalCap = baselineCapacity != null && o.capacity_requirement_percent != null
      ? baselineCapacity + o.capacity_requirement_percent : null;
    const effExposure = dailyExposure != null
      ? calculateEffectiveExposure(Number(dailyExposure), o.inaction_cost_effect) : null;
    const alignment = calculateAlignmentScore({
      is_constraint_break: o.is_constraint_break,
      is_strategy_shift: o.is_strategy_shift,
      total_capacity_percent: totalCap ?? undefined,
      reversibility_level: o.reversibility_level,
      inaction_cost_effect: o.inaction_cost_effect,
      commit_mode: commitMode,
      time_to_first_signal_days: o.time_to_first_signal_days,
      validation_deadline_days: validationDeadlineDays,
    });
    return { totalCap, effExposure, alignment };
  });

  const hasCapacityBaseline = baselineCapacity != null;
  const hasExposure = dailyExposure != null;

  const rows: Array<{
    label: string;
    render: (o: StrategicOption, i: number) => string;
    colorFn: (o: StrategicOption, i: number) => string;
    tooltip?: (o: StrategicOption, i: number) => string | undefined;
  }> = [
    {
      label: "Kosten",
      render: (o: StrategicOption) => formatEur(o.estimated_cost_min, o.estimated_cost_max),
      colorFn: () => "text-foreground",
    },
    {
      label: "Erstes Signal",
      render: (o: StrategicOption) => o.time_to_first_signal || "—",
      colorFn: (o: StrategicOption) => o.is_constraint_break ? "text-destructive" : "text-foreground",
    },
    {
      label: "Kapazität",
      render: (o: StrategicOption, i: number) => {
        const d = derivedMetrics[i];
        if (o.capacity_requirement_percent == null) return "—";
        if (d.totalCap != null) return `${o.capacity_requirement_percent}% (ges: ${d.totalCap}%)`;
        return `${o.capacity_requirement_percent}%`;
      },
      colorFn: (_o: StrategicOption, i: number) => {
        const d = derivedMetrics[i];
        return d.totalCap != null ? totalCapacityColor(d.totalCap) : capacityColor(_o.capacity_requirement_percent);
      },
      tooltip: hasCapacityBaseline ? (o: StrategicOption) => {
        if (o.capacity_requirement_percent == null || baselineCapacity == null) return undefined;
        return `Basis ${baselineCapacity}% (Phase 1) + ${o.capacity_requirement_percent}% (diese Option) = ${baselineCapacity + o.capacity_requirement_percent}% (kumuliert)`;
      } : undefined,
    },
    {
      label: "Reversibilität",
      render: (o: StrategicOption) => reversibilityLabel(o.reversibility_level),
      colorFn: (o: StrategicOption) => reversibilityColor(o.reversibility_level).split(" ")[0],
    },
    {
      label: "Inaction-Kosten",
      render: (o: StrategicOption) => inactionLabel(o.inaction_cost_effect).label,
      colorFn: (o: StrategicOption) => inactionLabel(o.inaction_cost_effect).color.split(" ")[0],
    },
  ];

  // Add effective burn row if baseline exists
  if (hasExposure && Number(dailyExposure) > 0) {
    rows.push({
      label: "Eff. Burn/Tag",
      render: (_o: StrategicOption, i: number) => {
        const d = derivedMetrics[i];
        return d.effExposure != null ? `${d.effExposure.toLocaleString("de-DE")} €` : "—";
      },
      colorFn: (_o: StrategicOption, i: number) => {
        const d = derivedMetrics[i];
        if (d.effExposure == null) return "text-muted-foreground";
        return d.effExposure < Number(dailyExposure) ? "text-emerald-600 dark:text-emerald-400" : "text-foreground";
      },
    });
  }

  // Alignment row
  rows.push({
    label: "Alignment",
    render: (_o: StrategicOption, i: number) => `${derivedMetrics[i].alignment.score}/100`,
    colorFn: (_o: StrategicOption, i: number) => derivedMetrics[i].alignment.color,
  });

  return (
    <Card className="border-border/50">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold text-primary uppercase tracking-wider">
            Ökonomischer Vergleich
          </span>
        </div>

        {/* Matrix Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50">
                <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground w-[140px]" />
                {options.map((o, i) => (
                  <th key={o.id} className="text-center py-2 px-3 text-xs font-bold text-foreground">
                    {labels[i]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-b border-border/30 last:border-0">
                  <td className="py-2.5 pr-4 text-xs font-medium text-muted-foreground whitespace-nowrap">
                    {row.label}
                  </td>
                  {options.map((o, i) => {
                    const tip = row.tooltip?.(o, i);
                    return (
                      <td key={o.id} className="text-center py-2.5 px-3">
                        {tip ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={cn("text-xs font-semibold cursor-help border-b border-dotted border-muted-foreground/30", row.colorFn(o, i))}>
                                  {row.render(o, i)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <p className="text-xs">{tip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className={cn("text-xs font-semibold", row.colorFn(o, i))}>
                            {row.render(o, i)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Kill Criterion row with tooltip */}
              <tr className="border-t border-border/50">
                <td className="py-2.5 pr-4 text-xs font-medium text-muted-foreground whitespace-nowrap">
                  Kipppunkt
                </td>
                {options.map((o) => (
                  <td key={o.id} className="text-center py-2.5 px-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-[11px] text-foreground/80 leading-tight block cursor-help line-clamp-2">
                            {o.kill_criterion_impact || "—"}
                          </span>
                        </TooltipTrigger>
                        {o.kill_criterion_impact && o.kill_criterion_impact.length > 60 && (
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="text-xs">{o.kill_criterion_impact}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Structural Observations */}
        {observations.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-border/50">
            <div className="flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                Strukturelle Beobachtungen
              </span>
            </div>
            {observations.map((obs, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="mt-1 h-1 w-1 rounded-full bg-muted-foreground/50 shrink-0" />
                <span>{obs}</span>
              </div>
            ))}
          </div>
        )}

        {/* Dynamic Completeness Block */}
        <div className="pt-2 border-t border-border/50">
          {(() => {
            const archetypes = options.map(o => o.archetype).filter(Boolean);
            const hasLowRisk = archetypes.includes("low_risk");
            const hasValueMax = archetypes.includes("value_max");
            const hasScaling = archetypes.includes("scaling");
            const covered: string[] = [];
            if (hasLowRisk) covered.push("Validierung");
            if (hasValueMax) covered.push("Traktion");
            if (hasScaling) covered.push("Langfrist-Architektur");
            const allCovered = hasLowRisk && hasValueMax && hasScaling;
            return (
              <p className="text-[11px] text-muted-foreground/70 italic">
                Vollständigkeit: Der Entscheidungsraum umfasst {covered.join(", ") || "—"}.
                {!allCovered && " ⚠ Nicht alle Archetypen abgedeckt."}
              </p>
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );
};
